import { AreaTypes, GeometryTypes } from "@/helpers/enums"
import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import DynamicModalData from "@/models/dynamicmodaldata"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import TableData from "@/models/tabledata"
import { Geometry } from "@turf/helpers"
import moment from "moment"
import AreaUtils from "@/helpers/areaUtils"
import Utils from "../utils/utils"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export interface Laajakaistahankkeet {
    id: number;
    hanke: string;
    julkaisuaika: string;
    kuntanumero: number | null;
    maakuntanumero: number | null;
    tila: string;
    lisatieto: string;
    koordinaatit: GeometryData;
    kuvaus: string | null;
    hakemus: string;
    rakennusvaihe: string;
    hankevaiheTeksti: string;
    operaattori: string;
    rightSlotData?: DynamicRightSlotData | null
    popup: string;
}

export interface Operaattorit {
    nimi: string;
    urlfi: string | null;
    urlsv: string | null;
    urlen: string | null;
}

export interface KVProperties {
    // [HuoneistotLkm]/[KaikkiHuoneistotLkm] *100
    prosentti: number;
    // dim_nopeusDown.Kielitermi
    nopeusteksti: string | null;
    nopeusid: number;
    lajittelu: string | null;
    minnopeus: number;
    maxnopeus: number;
}

export interface Hintatiedot {
    minkkhinta: number;
    maxkkhinta: number;
    minkytkentahinta: number;
    maxkytkentahinta: number;
    nopeusteksti: string;
    nopeustekstiup: string | null;
    lajittelu: string;
    julkaisuaika: string;
    nopeus: number;
    nopeusup: number;
}

export interface KVSaatavuus {
    nimi: string | null;
    namn: string | null;
    valmistumisaika: string | null;
    kuntanumero: string | null;
    maakuntanumero: string | null;
    tahtiluokitus: number;
    geoms: GeometryData | null;

    operaattorit: Operaattorit[];
    properties: KVProperties[];
    hintatiedot: Hintatiedot[] | null;

    rightSlotData?: DynamicRightSlotData | null
    popup: string;
}

export interface KVSaatavuusRuudut {
    koordinaatit: GeometryData;
    nopeus: string;

    rightSlotData?: DynamicRightSlotData | null
    popup: string;
}

export interface MVProperties {
    // [HuoneistotLkm]/[KaikkiHuoneistotLkm] *100
    prosentti: number;
    // dim_nopeusDown.Kielitermi
    nopeusteksti: string | null;
    nopeustekstitekniikka: string | null;
    nopeusid: number;
    tekniikkaid: number;
    lajittelu: string | null;
    minnopeus: number;
    maxnopeus: number;
}

export interface MVSaatavuus {
    nimi: string | null;
    namn: string | null;
    valmistumisaika: string | null;
    kuntanumero: string | null;
    maakuntanumero: string | null;
    tahtiluokitus: number;
    geoms: GeometryData | null;

    operaattorit: Operaattorit[];
    properties: MVProperties[];
    propertiesfiltered: MVProperties[];
    hintatiedot: Hintatiedot[] | null;

    rightSlotData?: DynamicRightSlotData | null
    popup: string;
}

export interface Palvelu {
    yleispalvelu: string;
    operaattorinimi: string;
}

export interface PaatosDTO {
    geometrygroupid: number;
    polygon: Geometry;
    palvelulista: Palvelu[];
    julkaisuaika: string;
}

export default class LaajakaistaJaPuhelinService {
    AreaUtils = new AreaUtils()

    get currentLanguage() {
        return store.getters.activeLang
    }

    // #region Laajakaistahankkeet
    /** Haetun datan käsittelyä: Laajakaistahankkeet.
    // muuttuvaa rightSlot data: julkaisuhetki,  kartan kohteella on lisätietoja jotka näytetään popupissa
    // eri värisiä alueita riippuen rakennusvaiheesta / legenda näytettävä  */
    async handleDataLaajakaistahankkeet(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()

            data.forEach((value) => {
                const options = {
                    fillColor: "green",
                    fillOpacity: 0.8,
                    color: "#6D6D6D",
                    opacity: 0.8,
                    weight: 1,
                    fill: true,
                    stroke: true,
                    id: value.id
                }
                /** täyttöväri hankkeen rakennusvaiheen mukaan */
                switch (value.rakennusvaihe.toLowerCase()) {
                    case "valmis":
                        options.fillColor = "#009FE366"
                        options.fillOpacity = 0.7
                        break
                    case "suunnitteilla":
                        options.fillColor = "#D6000066"
                        options.fillOpacity = 0.7
                        break
                    case "rakenteilla":
                        options.fillColor = "#FFAA0066"
                        options.fillOpacity = 0.7
                        break
                }
                value.koordinaatit.options = options
                value.koordinaatit.handleOnClick = true
                value.koordinaatit.allowedGeometryTypes = [GeometryTypes.GeometryCollection, GeometryTypes.MultiPolygon, GeometryTypes.Polygon]

                const rightSlotData = new DynamicRightSlotData(value.id.toString())
                rightSlotData.municipality = " "
                rightSlotData.dataUpdatedDate = " " // this.getUpdatedtext(value.julkaisuaika ?? moment().toISOString()) ?? "?"

                // Tukihakemuksen vaihe-teksti on ilmeisesti pääteltävä:
                // DIM_LKHakemus.Nimi ->
                // 1 Tukihakemus
                // 2 Lausunto
                // 3 Markkinaehtoinen,
                //  joista lopullinen teksti päätellään kohteen tilan perusteella: (Monitori1 projektista löytyi logiikka päättelyyn.
                // (Varmistettava vielä asiakkaalta?)

                if (value.hakemus === "Tukihakemus" && value.tila === "Valmis") {
                    value.hankevaiheTeksti = t("App.LaajakaistaJaPuhelin.Popup.Tukipäätösannettu") ?? ""
                }
                if (value.hakemus === "Tukihakemus" && (value.tila === "Odottaa" || value.tila === "Aktiivinen")) {
                    value.hankevaiheTeksti = t("App.LaajakaistaJaPuhelin.Popup.Tukipäätösvireillä") ?? ""
                }
                if (value.hakemus === "Lausunto" && value.tila === "Valmis") {
                    value.hankevaiheTeksti = t("App.LaajakaistaJaPuhelin.Popup.ELYlausuntoannettu") ?? ""
                }
                if (value.hakemus === "Lausunto" && (value.tila === "Odottaa" || value.tila === "Aktiivinen")) {
                    value.hankevaiheTeksti = t("App.LaajakaistaJaPuhelin.Popup.ELYlausuntovireillä") ?? ""
                }

                let popup = "<h3 class='h4'>" + value.hanke + "</h3>"
                if (value.hakemus !== "Markkinaehtoinen") {
                    popup += "<div>" + t("App.LaajakaistaJaPuhelin.Popup.Rakennusvaihe") + ": "
                        + this.getRakennusvaiheText(value.rakennusvaihe) + "<br/>"
                        + t("App.LaajakaistaJaPuhelin.Popup.Tukihakemuksenvaihe") + ": " + value.hankevaiheTeksti + "<br/>"
                    popup += t("App.LaajakaistaJaPuhelin.Popup.Yritys") + ": "
                    if (!value.lisatieto) {
                        popup += "<div>" + value.operaattori + "</div>"
                    }
                    else {
                        popup += "<a href='" + value.lisatieto + "' class='externallink' target='_blank'>" + value.operaattori
                            + "<img class='iframe-external-icon' src='" + this.getExternalLink() + "' alt='"
                            + this.getExternalLinkAlt() + "'>" + Utils.getLinkOpensNewTabSpan() + "</a>"
                    }
                    popup += "<br/></div>"
                }
                value.popup = popup
                apiData.push(new ApiDataModel(value.id.toString(), true, value.koordinaatit, value, rightSlotData, null))
            })

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    getUpdatedtext(time: string, includeText: boolean = true): string {
        const updatedtext = t("App.LaajakaistaJaPuhelin.DataUpdated")
        let updatedTime = "?"
        if (moment(time).format("MM/YYYY")) {
            updatedTime = t("App.Posti.DataUpdated") + " " + moment(time).format("MM/YYYY")
        }
        return includeText === true ? updatedtext + updatedTime : updatedTime
    }

    getRakennusvaiheText(value: string): string {
        switch (value.toLowerCase()) {
            case "valmis":
                return t("App.LaajakaistaJaPuhelin.Popup.Valmis") ?? ""
            case "suunnitteilla":
                return t("App.LaajakaistaJaPuhelin.Popup.Suunnitteilla") ?? ""
            case "rakenteilla":
                return t("App.LaajakaistaJaPuhelin.Popup.Rakenteilla") ?? ""
            default:
                return ""
        }
    }

    getLaajakaistahankkeetLegend(): string {
        let legend = "<div class=\"legend-item\"><i style=\"background: #ef9999\"></i><div>" + this.getRakennusvaiheText("suunnitteilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: #ffdd99\"></i><div>" + this.getRakennusvaiheText("rakenteilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: #99d9f4\"></i><div>" + this.getRakennusvaiheText("valmis") + "</div></div>"

        return legend
    }
    // #endregion

    // #region Kiinteälaajakaista
    async handleDataKiinteanLaajakaistanSaatavuus(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                const fastestSpeed = this.getKVFastestSpeed(value.properties)
                const color = this.getKVLegendColor(fastestSpeed)

                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.LaajakaistaJaPuhelin.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.getName(value)
                }
                rightSlotData.dataUpdatedDate = " " // this.getUpdatedtext(value.valmistumisaika ?? moment().toISOString(), true) ?? "?"
                rightSlotData.starRating = value.tahtiluokitus

                if (areaType === AreaTypes.Municipality) {
                    if (value.operaattorit.length > 0) {
                        const operatorList = this.createOperatorElements(value.operaattorit)
                        rightSlotData.textContent = (t("App.LaajakaistaJaPuhelin.KVlisatiedotlinktext") ?? "") + operatorList
                    }
                }

                rightSlotData.tableData = this.getSpeedTable(value.properties)
                if (areaType === AreaTypes.Country) {
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id)
            })
            return models
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    updateModels(models: ApiDataModel[], rightSlotData: DynamicRightSlotData, color: string, id: string) {
        const item = models.find(e => e.id === id)
        if (item !== undefined) {
            item.rightSlotData = rightSlotData
            item.featureData.koordinaatit.options.color = color
            item.featureData.koordinaatit.options.fillColor = color
            item.featureData.koordinaatit.options.fillOpacity = color === "" ? 0 : 0.8 // jos ei ole väriä niin täysi läpinäkyvyys
            if (item.geometryData !== null) {
                // Vain ne alueet voi valita kartalta, joilla on dataa
                item.geometryData.handleOnClick = true
            }
        }
    }

    getKVSaatavuusLegend(data): string {
        const areaType = this.AreaUtils.getAreaTypeFromData(data)

        if (areaType !== AreaTypes.Country) {
            let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(0) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Legend.ValtaosalleEiSaatavuutta") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(2) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download2M") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(10) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download10M") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(30) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download30M") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(100) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download100M") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(300) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download300M") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getKVLegendColor(1000) + "\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download1000M") + "</div></div>"

            return legend
        }

        return ""
    }

    // etsitään kohteen suurin nopus joka yli ttää 50% ja palautetaan sen arvo -> arvolla voidaan hakea sitten oikea väri
    getKVFastestSpeed(properties: KVProperties[]): number {
        let fastestSpeed = 0
        properties.forEach((value) => {
            if (value.prosentti > 50) {
                if (value.minnopeus > fastestSpeed) {
                    fastestSpeed = value.minnopeus
                }
            }
        })
        return fastestSpeed
    }

    getSpeedTable(properties: KVProperties[]): TableData {
        return new TableData([t("App.LaajakaistaJaPuhelin.Nopeus") ?? "", t("App.LaajakaistaJaPuhelin.Saatavuus") ?? ""], [
            [t("App.LaajakaistaJaPuhelin.Download2M") ?? "", this.getKVMinnopeus(2, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")],
            [t("App.LaajakaistaJaPuhelin.Download10M") ?? "", this.getKVMinnopeus(10, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")],
            [t("App.LaajakaistaJaPuhelin.Download30M") ?? "", this.getKVMinnopeus(30, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")],
            [t("App.LaajakaistaJaPuhelin.Download100M") ?? "", this.getKVMinnopeus(100, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")],
            [t("App.LaajakaistaJaPuhelin.Download300M") ?? "", this.getKVMinnopeus(300, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")],
            [t("App.LaajakaistaJaPuhelin.Download1000M") ?? "", this.getKVMinnopeus(1000, properties).toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")]
        ])
    }

    getKVMinnopeus(count: number, properties: KVProperties[]): number {
        // palauttaa 0, jos undefined
        const nopeusSaatavuus = properties.find(x => x.minnopeus === count)
        if (nopeusSaatavuus !== undefined) {
            return nopeusSaatavuus.prosentti
        }
        else {
            return 0
        }
    }

    getKVLegendColor(count: number): string {
        // kunnalla/maakunnalla ei ole dataa mikäli parametrin arvo === 0
        if (count === 0) {
            return ""
        }
        else if (count === 2) {
            return "#E2E2E2CC"
        }
        else if (count === 10) {
            return "#bfb3b9CC"
        }
        else if (count === 30) {
            return "#d5a0c1CC"
        }
        else if (count === 100) {
            return "#da73a9CC"
        }
        else if (count === 300) {
            return "#dd348eCC"
        }
        else if (count === 1000) {
            return "#b32f75CC"
        }
        else if (count > 1000) {
            return "#b32f75CC"
        }
        return ""
    }

    /** Haetaan valitun kielen mukaan oikea nimi */
    getName(value) {
        let newName: string = ""

        switch (this.currentLanguage) {
            case "fi":
                newName = value.nimi ?? "-"
                break
            case "sv":
                newName = value.namn ?? "-"
                break
            case "en":
                newName = value.nimi ?? "-"
                break
            default:
                newName = value.nimi ?? "-"
                break
        }

        return newName
    }

    /** Haetaan valitun kielen mukaan oikea linkki */
    getUrl(value) {
        let newUrl: string = ""

        switch (this.currentLanguage) {
            case "fi":
                newUrl = value.urlfi ?? "#"
                break
            case "sv":
                newUrl = value.urlsv ?? "#"
                break
            case "en":
                newUrl = value.urlen ?? "#"
                break
            default:
                newUrl = value.urlfi ?? "#"
                break
        }

        return newUrl
    }

    getExternalLink(): string {
        return store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + store.getters.contentServiceTimeStamp
    }

    getExternalLinkAlt(): string | undefined {
        return t("App.Link.UlosJohtavaLinkki")
    }

    get modalHeader(): string {
        return store.getters.header
    }

    async handleDataKiinteanLaajakaistanHinnat(data): Promise<void> {
        try {
            const textContent = this.getHintatiedotTextContent(data.hintatiedot ?? null)

            const tableData = this.getPriceTable(data.hintatiedot ?? [])
            // avataan modaali tarkempien tietojen näyttämiseksi
            const modalData = new DynamicModalData("lisatiedotkunnanlaajakaistaliittymista", this.modalHeader, textContent, null, tableData)
            store.dispatch("setModalData", modalData)
            this.openModal()
        }
        catch (err) {
            console.log(err)
        }
    }

    getHintatiedotTextContent(properties: Hintatiedot[] | null): string {
        const basicText = t("App.LaajakaistaJaPuhelin.LisatiedotKunnanLaajakaistaLiittymistaText")
        const extraText = t("App.LaajakaistaJaPuhelin.LisatiedotKunnanLaajakaistaLiittymistaText2")
        const updatedDate = properties !== null && properties.length !== 0 ? moment(properties[0].julkaisuaika).format("DD.MM.YYYY") ?? "-" : "-"
        const updatedtext = "<b>" + t("App.LaajakaistaJaPuhelin.DataUpdated") + "</b> " + updatedDate

        return "<p>" + basicText + "</p><p>" + updatedtext + "</p><p>" + extraText + "</p>"
    }

    getPriceTable(properties: Hintatiedot[]): TableData {
        const tableRowData = new Array<Array<string>>()

        properties.forEach((value) => {
            let nopeusText = ""
            const nopeusteksti = value.nopeusteksti.replace(/\./g, "")
            const nopeustekstiUp = value.nopeustekstiup ?? ""
            // jos lähetys nopeuskin on mukana niin sitten pitää olla muotoa 100/100 Mbit/s.
            if (nopeustekstiUp) {
                // näytettävän tekstin loppuosa
                const nopeustemp = t("App.LaajakaistaJaPuhelin.Mbits") ?? "App.LaajakaistaJaPuhelin.Mbits"
                nopeusText = value.nopeus.toString() + "/" + value.nopeusup.toString() + " " + nopeustemp
            }
            else {
                // pelkkä latausnopeus joten voidaan käyttää suoraan nopeustekstiä hakemaan käännöstermi
                nopeusText = t("App.LaajakaistaJaPuhelin." + nopeusteksti) ?? "-"
            }

            const u = new Utils()
            const hinta = u.formatPrice(value.minkkhinta).toString() + " - " + u.formatPrice(value.maxkkhinta).toString() + " " + t("App.LaajakaistaJaPuhelin.€/kk")
            const aloitus = u.formatPrice(value.minkytkentahinta).toString() + " - " + u.formatPrice(value.maxkytkentahinta).toString() + " €"
            const rivi: string[] = [nopeusText, hinta, aloitus]
            tableRowData.push(rivi)
        })
        return new TableData([t("App.LaajakaistaJaPuhelin.Nopeus") ?? "App.LaajakaistaJaPuhelin.Nopeus",
        t("App.LaajakaistaJaPuhelin.Hinta") ?? "App.LaajakaistaJaPuhelin.Hinta",
        t("App.LaajakaistaJaPuhelin.Aloitusmaksu") ?? "App.LaajakaistaJaPuhelin.Hinta"],
            tableRowData)
    }

    openModal() {
        store.dispatch("setModalComponentName", "LisatiedotKunnanLaajakaistaliittymistaModal")
    }

    // #endregion

    // #region Mobiili
    async handleDataMobiiliverkonKattavuus(data): Promise<ApiDataModel[]> {
        try {
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                const color = this.getMVLegendColor(value.propertiesfiltered[0].prosentti)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.LaajakaistaJaPuhelin.EntireFinland") ?? ""
                }
                else {
                    rightSlotData.municipality = this.getName(value)
                }
                rightSlotData.dataUpdatedDate = " " // this.getUpdatedtext(value.valmistumisaika ?? moment().toISOString(), true) ?? "?"
                rightSlotData.starRating = value.tahtiluokitus

                if (areaType === AreaTypes.Municipality) {
                    if (value.operaattorit.length > 0) {
                        const operatorList = this.createOperatorElements(value.operaattorit)
                        rightSlotData.textContent = (t("App.LaajakaistaJaPuhelin.MVlisatiedotlinktext") ?? "") + operatorList
                    }
                }

                rightSlotData.tableData = this.getSpeedTableMV(value.properties)
                if (areaType === AreaTypes.Country) {
                    store.dispatch("setRightSlotData", rightSlotData)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id)
            })
            return models
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    getSpeedTableMV(properties: MVProperties[]): TableData {
        const tableRowData = new Array<Array<string>>()
        properties.forEach(element => {
            const tekniikkaText = t("App.LaajakaistaJaPuhelin." + element.nopeustekstitekniikka) ?? "-"
            const nopeusteksti = element.nopeusteksti?.replace(/\./g, "")
            const nopeusText = t("App.LaajakaistaJaPuhelin." + nopeusteksti) ?? "-"
            const nopeus = tekniikkaText + " " + nopeusText
            const saatavuus = element.prosentti.toString() + " % " + t("App.LaajakaistaJaPuhelin.Kodeista")
            const rivi: string[] = [nopeus, saatavuus]
            tableRowData.push(rivi)
        })

        return new TableData([t("App.LaajakaistaJaPuhelin.Nopeus") ?? "", t("App.LaajakaistaJaPuhelin.Saatavuus") ?? ""], tableRowData)
    }

    getMVSaatavuusLegend(data): string {
        const areaType = this.AreaUtils.getAreaTypeFromData(data)

        if (areaType !== AreaTypes.Country) {
            const legendText = " % " + t("App.LaajakaistaJaPuhelin.Kodeista")
            let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(0) + "\"></i><div>" + t("App.TieLiikenne.Legend.EiKattavuutta") + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(10) + "\"></i><div> < 85" + legendText + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(85) + "\"></i><div> 85 - 89" + legendText + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(90) + "\"></i><div> 90 - 94" + legendText + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(95) + "\"></i><div> 95 - 98" + legendText + "</div></div>"
            legend += "<div class=\"legend-item\"><i style=\"background: " + this.getMVLegendColor(100) + "\"></i><div> > 98" + legendText + "</div></div>"

            return legend
        }

        return ""
    }

    async handleDataMobiiliverkonHinnat(data): Promise<void> {
        try {
            const textContent = this.getHintatiedotTextContent(data.hintatiedot ?? null)

            const tableData = this.getPriceTable(data.hintatiedot ?? [])
            // avataan modaali tarkempien tietojen näyttämiseksi
            const modalData = new DynamicModalData("lisatiedotkunnanmobiililiittymista", this.modalHeader, textContent, null, tableData)
            store.dispatch("setModalData", modalData)
            this.openModal()
        }
        catch (err) {
            console.log(err)
        }
    }

    getMVLegendColor(count: number): string {
        // kunnalla/maakunnalla ei ole dataa mikäli parametrin arvo === 0
        if (count === 0) {
            return ""
        }
        if (count < 85) {
            return "#e2e2e2"
        }
        else if (count >= 85 && count <= 89) {
            return "#bfb3b9"
        }
        else if (count >= 90 && count <= 94) {
            return "#d5a0c1"
        }
        else if (count >= 95 && count <= 98) {
            return "#da73a9"
        }
        else if (count > 98) {
            return "#dd348e"
        }
        return ""
    }

    // #endregion
    get getTableColumHeadersForOikeus(): string[] {
        const columns: string[] = []
        columns.push(t("App.OikeusPuhelinJaLaajakaistaLiittymaan.TableHeader.Yleispalvelu") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.TableHeader.Yleispalvelu")
        columns.push(t("App.OikeusPuhelinJaLaajakaistaLiittymaan.TableHeader.OperaattoriNimi") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.TableHeader.OperaattoriNimi")
        return columns
    }

    async handleDataOikeus(data, sijainti): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            const tableHeader = this.getTableColumHeadersForOikeus
            const eiNimi = t("App.OikeusPuhelinJaLaajakaistaLiittymaan.OperaattoriNimi.EiNimi") ?? ""

            data.forEach((item) => {
                const id = item.geometrygroupid ?? 1
                const options = {
                    fillColor: "#009FE366",
                    fillOpacity: 0.8,
                    color: "#6D6D6D",
                    opacity: 0.8,
                    weight: 1,
                    fill: true,
                    stroke: true,
                    id
                }

                let koordinaatit: GeometryData | null
                if (item.polygon !== null) {
                    koordinaatit = { type: item.polygon.type, options, coordinates: item.polygon.coordinates, markerOptions: null, handleOnClick: true, allowedGeometryTypes: null }
                }
                else {
                    koordinaatit = null
                }
                const rightSlotData = new DynamicRightSlotData(id.toString())
                // rightSlotData.dataUpdatedDate = updatedtext
                let tableRowData = new Array<Array<string>>()

                item.palvelulista.forEach((palvelu) => {
                    if (palvelu.operaattorinimi === "") {
                        palvelu.operaattorinimi = eiNimi
                    }
                })

                if (item.palvelulista && item.palvelulista.length > 0) {
                    item.palvelulista.forEach(x => {
                        switch (x.yleispalvelu.toLocaleLowerCase()) {
                            case "tekstiviestipalvelu":
                                x.yleispalvelu = t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Tekstiviestipalvelu") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Tekstiviestipalvelu"
                                break
                            case "puhelinpalvelu":
                                x.yleispalvelu = t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Puhelinpalvelu") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Puhelinpalvelu"
                                break
                            case "512k512klaajakaista":
                                x.yleispalvelu = t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Laajakaista") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.Laajakaista"
                                break
                            case "5mlaajakaista":
                                x.yleispalvelu = t("App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.5Mlaajakaista") ?? "App.OikeusPuhelinJaLaajakaistaLiittymaan.DropDownList.5Mlaajakaista"
                                break
                        }
                    })
                    tableRowData = item.palvelulista
                }
                const tableData = new TableData(tableHeader, tableRowData)
                rightSlotData.tableData = tableData
                if (sijainti) {
                    store.dispatch("setRightSlotData", rightSlotData)
                }
                else {
                    apiData.push(new ApiDataModel(id.toString(), true, koordinaatit, item, rightSlotData, null)) // laitetaan koko data talteen
                }
            })
            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    async handleKiinteanVerkonSaatavuusRuudut(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()

            data.forEach((item, index) => {
                const options = {
                    fillColor: "#2d00f7",
                    fillOpacity: 0.8,
                    color: "#666",
                    opacity: 0.8,
                    weight: 1,
                    fill: true,
                    stroke: true,
                    id: index
                }

                switch (item.nopeus.toString()) {
                    case "100":
                        options.fillColor = "#DF78AF"
                        options.fillOpacity = 0.8
                        break
                    case "1000":
                        options.fillColor = "#B8337A"
                        options.fillOpacity = 0.8
                        break
                }

                item.koordinaatit.options = options
                item.koordinaatit.allowedGeometryTypes = [GeometryTypes.Polygon]
                apiData.push(new ApiDataModel(index.toString(), true, item.koordinaatit, item, null, null))
            })

            if (data.length > 0) {
                // Tiedot päivitetty teksti
                const rightSlotData = new DynamicRightSlotData("1")
                store.dispatch("setRightSlotData", rightSlotData)
            }

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    getKVSaatavuusRuudutLegend(): string {
        let legend = "<div class=\"legend-item\"><i style=\"background: #DF78AF\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download100M") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: #B8337A\"></i><div>" + t("App.LaajakaistaJaPuhelin.Download1000M") + "</div></div>"

        return legend
    }

    createOperatorElements(operaattorit) {
        const operators: any[] = []

        operaattorit.forEach((o) => {
            let operatorElement: any = null

            if (this.getUrl(o)) { // Lisätään hyperlink-elementti, jonka sisällä ulkoinen linkki -ikoni
                const extrenlinkIcon = this.getExternalLinkIcon()

                operatorElement = document.createElement("a")
                operatorElement.href = this.makeAbsoluteURL(this.getUrl(o))
                operatorElement.setAttribute("target", "_blank")
                operatorElement.classList.add("open-modal-link")
                operatorElement.textContent = "\u00A0" + o.nimi + "\u00A0"
                operatorElement.appendChild(extrenlinkIcon)

                operators.push(operatorElement.outerHTML)
            }
            else if (o.nimi) { // Lisätään paragraph-elementtinä, mikäli urlia ei löydy
                operatorElement = "\u00A0" + o.nimi + "\u00A0"

                operators.push(operatorElement)
            }           
        })

        return operators
    }

    getExternalLinkIcon(): HTMLImageElement {
        const icon = document.createElement("img")
        icon.src = this.getExternalLink()
        icon.alt = this.getExternalLinkAlt() ?? ""
        return icon
    }

    // Tehdään relatiivisista linkeistä absoluuttisia,
    // jotta linkit eivät avautuisi sovelluksen ympäristöön
    makeAbsoluteURL(url: string): string {
        if (!url.match(/^https?:\/\//i)) {
            url = "http://" + url
        }
        return url
    }
}
